/** Pricing — LOTE M. URL: /pricing. Planes actualizados 2026-08-08. */
import { useEffect, useState } from 'react';
import { useWww } from './_layout';
import { wwwTheme, wwwRadius, wwwFonts, wwwAccent } from '@/src/website/theme';
import { wwwFormat, type WwwDict } from '@/src/website/i18n';
import { PLANS_V2_PUBLIC } from '@/src/website/launch';
import { SeoHead } from '@/src/website/SeoHead';

// Web equivalent of RN's useWindowDimensions().
function useWindowDimensions() {
  const [size, setSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  }));
  useEffect(() => {
    const onResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return size;
}

type Amount = number | 'unlimited';
interface Plan {
  id: 'free' | 'premium' | 'plus';
  price: string;
  items: Amount; opts: number; out: Amount; try: Amount; chat: Amount; col: Amount;
  shopTry?: number;
  aiTier: 'standard' | 'advanced' | 'top';
  travel: boolean; memory: boolean; profile: 'teaser' | 'full';
  support: boolean; early: boolean;
  save?: number;
}

const PLANS: Record<'monthly' | 'annual', Plan[]> = {
  monthly: [
    { id: 'free',    price: '0',     items: 50,          opts: 2, out: 5,           try: 5,           chat: 5,           col: 10,          aiTier: 'standard', travel: false, memory: false, profile: 'teaser', support: false, early: false },
    { id: 'premium', price: '6,99',  items: 200,         opts: 4, out: 100,         try: 100,         chat: 100,         col: 'unlimited', aiTier: 'advanced', travel: true,  memory: true,  profile: 'full',   support: true,  early: false },
    { id: 'plus',    price: '12,99', items: 'unlimited', opts: 6, out: 'unlimited', try: 'unlimited', chat: 'unlimited', col: 'unlimited', aiTier: 'top',      travel: true,  memory: true,  profile: 'full',   support: true,  early: true },
  ],
  annual: [
    { id: 'free',    price: '0',     items: 50,          opts: 2, out: 5,           try: 5,           chat: 5,           col: 10,          aiTier: 'standard', travel: false, memory: false, profile: 'teaser', support: false, early: false },
    { id: 'premium', price: '59,99', items: 200,         opts: 4, out: 100,         try: 100,         chat: 100,         col: 'unlimited', aiTier: 'advanced', travel: true,  memory: true,  profile: 'full',   support: true,  early: false, save: 28 },
    { id: 'plus',    price: '99,99', items: 'unlimited', opts: 6, out: 'unlimited', try: 'unlimited', chat: 'unlimited', col: 'unlimited', aiTier: 'top',      travel: true,  memory: true,  profile: 'full',   support: true,  early: true,  save: 35 },
  ],
};

// STILGIA 2.0.1 approved V2 quotas — rendered only when PLANS_V2_PUBLIC is true.
const PLANS_V2: Record<'monthly' | 'annual', Plan[]> = {
  monthly: [
    { id: 'free',    price: '0',     items: 50,          opts: 2, out: 5,  try: 5,  shopTry: 10, chat: 5,           col: 10,          aiTier: 'standard', travel: false, memory: false, profile: 'teaser', support: false, early: false },
    { id: 'premium', price: '6,99',  items: 200,         opts: 4, out: 50, try: 40, shopTry: 40, chat: 100,         col: 'unlimited', aiTier: 'advanced', travel: true,  memory: true,  profile: 'full',   support: true,  early: false },
    { id: 'plus',    price: '12,99', items: 'unlimited', opts: 6, out: 90, try: 75, shopTry: 40, chat: 'unlimited', col: 'unlimited', aiTier: 'top',      travel: true,  memory: true,  profile: 'full',   support: true,  early: true },
  ],
  annual: [
    { id: 'free',    price: '0',     items: 50,          opts: 2, out: 5,  try: 5,  shopTry: 10, chat: 5,           col: 10,          aiTier: 'standard', travel: false, memory: false, profile: 'teaser', support: false, early: false },
    { id: 'premium', price: '59,99', items: 200,         opts: 4, out: 50, try: 40, shopTry: 40, chat: 100,         col: 'unlimited', aiTier: 'advanced', travel: true,  memory: true,  profile: 'full',   support: true,  early: false, save: 28 },
    { id: 'plus',    price: '99,99', items: 'unlimited', opts: 6, out: 90, try: 75, shopTry: 40, chat: 'unlimited', col: 'unlimited', aiTier: 'top',      travel: true,  memory: true,  profile: 'full',   support: true,  early: true,  save: 35 },
  ],
};

function amountLabel(template: string, value: Amount, t: WwwDict): string {
  if (value !== 'unlimited') return wwwFormat(template, { n: value });
  const label = template.replace('{n}', '').trim();
  return label.charAt(0).toUpperCase() + label.slice(1) + ' — ' + t.pricingUnlimited;
}

export default function Pricing() {
  const { t, lang } = useWww();
  const { width } = useWindowDimensions();
  const isMobile = width < 760;
  const [interval, setInterval] = useState<'monthly' | 'annual'>('monthly');
  const plans = (PLANS_V2_PUBLIC ? PLANS_V2 : PLANS)[interval];
  const labelFor = (id: string) => id === 'free' ? t.pricingFreeName : id === 'premium' ? t.pricingPremiumName : t.pricingPlusName;
  const descFor  = (id: string) => id === 'free' ? t.pricingFreeDesc : id === 'premium' ? t.pricingPremiumDesc : t.pricingPlusDesc;
  const aiLabelFor = (tier: Plan['aiTier']) => tier === 'standard' ? t.pricingStandardAI : tier === 'advanced' ? t.pricingAdvancedAI : t.pricingTopAI;

  // Web replacement for <Stack.Screen options={{ title: ... }} />: update
  // document.title so the browser tab matches the string the native screen
  // header used in the Expo version. Runs after SeoHead's effect so it has
  // the final word, exactly as in the original render order.
  useEffect(() => {
    document.title = t.pricingTitle + ' · STILGIA';
  }, [t.pricingTitle]);

  return (
    <>
      <SeoHead page="pricing" locale={lang} />

      <div style={{ paddingLeft: isMobile ? 24 : 48, paddingRight: isMobile ? 24 : 48, paddingTop: 64, paddingBottom: 24, maxWidth: 1180, width: '100%', marginLeft: 'auto', marginRight: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', boxSizing: 'border-box' }}>
        <span data-testid="pricing-free-badge" style={{ display: 'block', color: wwwAccent, fontSize: 12, fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 12 }}>{t.pricingFreeBadge}</span>
        <h1 style={{ color: wwwTheme.text, fontFamily: wwwFonts.serifBold, fontSize: isMobile ? 34 : 48, textAlign: 'center', marginTop: 0, marginBottom: 0 }}>{t.pricingTitle}</h1>
        <p style={{ color: wwwTheme.muted, fontSize: 16, marginTop: 14, marginBottom: 0, textAlign: 'center', maxWidth: 620 }}>{t.pricingSubtitle}</p>
        <div style={{ display: 'flex', flexDirection: 'row', backgroundColor: wwwTheme.surface, borderColor: wwwTheme.border, borderWidth: 1, borderStyle: 'solid', borderRadius: wwwRadius.pill, padding: 4, marginTop: 28 }}>
          {(['monthly','annual'] as const).map(iv => (
            <button key={iv} data-testid={`pricing-interval-${iv}`} onClick={() => setInterval(iv)} style={{ paddingLeft: 20, paddingRight: 20, paddingTop: 10, paddingBottom: 10, borderRadius: wwwRadius.pill, backgroundColor: interval === iv ? wwwTheme.text : 'transparent', border: 'none', cursor: 'pointer' }}>
              <span style={{ color: interval === iv ? wwwTheme.onPrimary : wwwTheme.muted, fontWeight: 600, fontSize: 13 }}>{iv === 'monthly' ? t.pricingMonthly : t.pricingAnnual}</span>
            </button>
          ))}
        </div>
      </div>

      <div style={{ paddingLeft: isMobile ? 24 : 48, paddingRight: isMobile ? 24 : 48, paddingBottom: 64, maxWidth: 1180, width: '100%', marginLeft: 'auto', marginRight: 'auto', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 18, alignItems: 'stretch' }}>
          {plans.map(p => {
            const highlight = p.id === 'premium';
            return (
              <div key={p.id} data-testid={`pricing-plan-${p.id}`} style={{ flex: 1, backgroundColor: wwwTheme.surface, borderColor: highlight ? wwwTheme.text : wwwTheme.border, borderWidth: highlight ? 2 : 1, borderStyle: 'solid', borderRadius: wwwRadius.xl, padding: 28, display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
                <h2 style={{ color: wwwTheme.text, fontSize: 18, fontWeight: 700, marginTop: 0, marginBottom: 0 }}>{labelFor(p.id)}</h2>
                <p style={{ color: wwwTheme.muted, fontSize: 13, marginTop: 6, marginBottom: 0, minHeight: 36 }}>{descFor(p.id)}</p>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline', marginTop: 18, gap: 6, flexWrap: 'wrap' }}>
                  <span data-testid={`pricing-price-${p.id}`} style={{ color: wwwTheme.text, fontSize: 38, fontWeight: 700, fontFamily: wwwFonts.serifBold }}>{p.price === '0' ? '0 €' : p.price + ' €'}</span>
                  <span style={{ color: wwwTheme.muted, fontSize: 13 }}>{interval === 'monthly' ? t.pricingPerMonth : t.pricingPerYear}</span>
                  {p.save && (
                    <div style={{ backgroundColor: '#16A34A22', paddingLeft: 8, paddingRight: 8, paddingTop: 3, paddingBottom: 3, borderRadius: 999, marginLeft: 8 }}>
                      <span style={{ color: '#16A34A', fontSize: 12, fontWeight: 700 }}>{wwwFormat(t.pricingSaveBadge, { pct: p.save })}</span>
                    </div>
                  )}
                </div>
                <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <Bullet ok>{amountLabel(t.pricingItems, p.items, t)}</Bullet>
                  <Bullet ok>{wwwFormat(t.pricingOutfitOptions, { n: p.opts })}</Bullet>
                  {PLANS_V2_PUBLIC ? (
                    <>
                      <Bullet ok>{wwwFormat(t.pricingTryonsV2, { n: p.try as number })}</Bullet>
                      <Bullet ok>{wwwFormat(t.pricingShopTryonsV2, { n: p.shopTry as number })}</Bullet>
                      <Bullet ok>{wwwFormat(t.pricingOutfitsV2, { n: p.out as number })}</Bullet>
                    </>
                  ) : (
                    <>
                      <Bullet ok>{amountLabel(t.pricingOutfits, p.out, t)}</Bullet>
                      <Bullet ok>{amountLabel(t.pricingTryons, p.try, t)}</Bullet>
                    </>
                  )}
                  <Bullet ok>{amountLabel(t.pricingStylistChats, p.chat, t)}</Bullet>
                  <Bullet ok>{amountLabel(t.pricingCollections, p.col, t)}</Bullet>
                  <Bullet ok={p.travel}>{t.pricingTravel}</Bullet>
                  <Bullet ok={p.memory}>{t.pricingStyleMemory}</Bullet>
                  <Bullet ok>{p.profile === 'full' ? t.pricingStyleProfileFull : t.pricingStyleProfileTeaser}</Bullet>
                  <Bullet ok>{aiLabelFor(p.aiTier)}</Bullet>
                  <Bullet ok>{t.pricingShopSearch}</Bullet>
                  <Bullet ok>{t.pricingCalendar}</Bullet>
                  <Bullet ok>{t.pricingWeather}</Bullet>
                  <Bullet ok={p.support}>{t.pricingSupport}</Bullet>
                  <Bullet ok={p.early}>{t.pricingEarlyAccess}</Bullet>
                </div>
              </div>
            );
          })}
        </div>
        <p data-testid="pricing-footnote" style={{ color: wwwTheme.muted, fontSize: 12, marginTop: 28, marginBottom: 0, lineHeight: '18px' }}>{t.pricingFootnote}</p>
      </div>
    </>
  );
}

function Bullet({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'flex-start' }}>
      <span style={{ color: ok ? wwwTheme.text : wwwTheme.muted, fontSize: 14, opacity: ok ? 1 : 0.5 }}>{ok ? '✓' : '—'}</span>
      <span style={{ color: ok ? wwwTheme.text : wwwTheme.muted, fontSize: 13, lineHeight: '19px', flex: 1 }}>{children}</span>
    </div>
  );
}
